/* global $ */
import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import marketplaceApi from '../marketplaceApi';

const STATUS_TABS = ['all', 'active', 'unlisted', 'sold'];
const STATUS_COLOR = { active: 'success', unlisted: 'warning', sold: 'secondary' };

function fmt(n) { return n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—'; }
function timeAgo(iso) {
  if (!iso) return '—';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d === 0) return 'Today'; if (d === 1) return 'Yesterday';
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MarketplaceListings() {
  const [listings,   setListings]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [tab,        setTab]        = useState('all');
  const [loading,    setLoading]    = useState(true);
  const [updating,   setUpdating]   = useState(null);
  const [available,  setAvailable]  = useState([]);
  const [avLoading,  setAvLoading]  = useState(false);
  const [enrolling,  setEnrolling]  = useState(null);
  const [enrolled,   setEnrolled]   = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (tab !== 'all') params.status = tab;
      const { data } = await marketplaceApi.get('/listings', { params });
      setListings(data.listings || []);
      setTotal(data.total || 0);
      setPages(data.totalPages || 1);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await marketplaceApi.patch(`/listings/${id}`, { status });
      setListings(ls => ls.map(l => l.id === id ? { ...l, marketplaceStatus: status } : l));
    } catch {
      Swal.fire('Error', 'Failed to update status', 'error');
    } finally { setUpdating(null); }
  };

  const openAddModal = async () => {
    setEnrolled(new Set());
    setAvLoading(true);
    $('#addListingModal').modal('show');
    try {
      const { data } = await marketplaceApi.get('/listings/available');
      setAvailable(data.vehicles || []);
    } catch { setAvailable([]); }
    finally { setAvLoading(false); }
  };

  const enroll = async (id) => {
    setEnrolling(id);
    try {
      await marketplaceApi.post(`/listings/${id}/enroll`);
      setEnrolled(s => new Set(s).add(id));
      load();
    } catch {
      Swal.fire('Error', 'Failed to publish vehicle', 'error');
    } finally { setEnrolling(null); }
  };

  const columns = [
    {
      name: 'Vehicle',
      cell: row => (
        <div>
          <strong>{row.vehicle_name} {row.model}</strong>
          <div className="text-muted small">{row.make_year} · {row.dealer_city}</div>
        </div>
      ),
      minWidth: '180px',
    },
    { name: 'Price', selector: row => fmt(row.sell_price), width: '110px' },
    {
      name: 'Details',
      cell: row => (
        <small className="text-muted">
          {row.km_driven != null ? `${Number(row.km_driven).toLocaleString('en-IN')} km` : '—'}
          {' · '}{row.noofowners} owner{row.noofowners !== 1 ? 's' : ''}
          {row.fuel_type ? ` · ${row.fuel_type}` : ''}
        </small>
      ),
      minWidth: '160px',
    },
    {
      name: 'Leads',
      cell: row => row.leadCount > 0
        ? <span className="badge badge-success">{row.leadCount}</span>
        : <span className="text-muted">—</span>,
      width: '70px',
    },
    {
      name: 'Status',
      cell: row => (
        <span className={`badge badge-${STATUS_COLOR[row.marketplaceStatus] || 'secondary'}`}>
          {row.marketplaceStatus}
        </span>
      ),
      width: '90px',
    },
    { name: 'Published', selector: row => timeAgo(row.publishedAt), width: '110px' },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-1">
          {updating === row.id
            ? <span className="spinner-border spinner-border-sm text-secondary" />
            : (
              <>
                {row.marketplaceStatus === 'active' && (
                  <>
                    <button onClick={() => updateStatus(row.id, 'unlisted')} className="btn btn-xs btn-warning">Unlist</button>
                    <button onClick={() => updateStatus(row.id, 'sold')} className="btn btn-xs btn-success">Sold</button>
                  </>
                )}
                {row.marketplaceStatus === 'unlisted' && (
                  <button onClick={() => updateStatus(row.id, 'active')} className="btn btn-xs btn-primary">Relist</button>
                )}
                {row.marketplaceStatus === 'sold' && (
                  <button onClick={() => updateStatus(row.id, 'unlisted')} className="btn btn-xs btn-secondary">Unlist</button>
                )}
              </>
            )
          }
        </div>
      ),
      width: '130px',
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <h1 className="m-0"><i className="fas fa-list mr-2 text-success" />My Listings</h1>
          <button className="btn btn-success" onClick={openAddModal}>
            <i className="fas fa-plus mr-2" />Add Listing
          </button>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">

          {/* Status tabs */}
          <ul className="nav nav-tabs mb-3">
            {STATUS_TABS.map(t => (
              <li className="nav-item" key={t}>
                <button
                  className={`nav-link${tab === t ? ' active' : ''}`}
                  onClick={() => { setTab(t); setPage(1); }}
                  style={{ textTransform: 'capitalize' }}
                >
                  {t}
                </button>
              </li>
            ))}
          </ul>

          <div className="card">
            <div className="card-header">
              <span className="text-muted">{total} listing{total !== 1 ? 's' : ''}</span>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={listings}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={total}
                paginationPerPage={20}
                onChangePage={p => setPage(p)}
                noDataComponent={
                  <div className="py-5 text-center text-muted">
                    <i className="fas fa-motorcycle fa-2x mb-2 d-block" />
                    No listings found.{' '}
                    <button className="btn btn-link p-0" onClick={openAddModal}>Add one now</button>
                  </div>
                }
              />
            </div>
          </div>

        </div>
      </section>

      {/* Add Listing Modal */}
      <div className="modal fade" id="addListingModal" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title"><i className="fas fa-plus-circle mr-2" />Add Listing</h4>
              <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">Select vehicles to publish on the PickMyWheels marketplace.</p>
              {avLoading ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
              ) : available.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-check-circle fa-2x mb-2 d-block text-success" />
                  All vehicles are already published!
                </div>
              ) : (
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Vehicle</th><th>Price</th><th>KM</th><th>City</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {available.map(v => {
                      const done = enrolled.has(v.id);
                      const busy = enrolling === v.id;
                      return (
                        <tr key={v.id}>
                          <td>
                            <strong>{v.vehicle_name} {v.model}</strong>
                            <div className="text-muted small">{v.make_year} · {v.fuel_type}</div>
                          </td>
                          <td>{fmt(v.sell_price)}</td>
                          <td>{v.km_driven != null ? `${Number(v.km_driven).toLocaleString('en-IN')} km` : '—'}</td>
                          <td>{v.dealer_city}</td>
                          <td>
                            <button
                              disabled={done || busy}
                              onClick={() => enroll(v.id)}
                              className={`btn btn-sm ${done ? 'btn-outline-success' : 'btn-success'}`}
                            >
                              {busy ? <span className="spinner-border spinner-border-sm" /> : done ? <><i className="fas fa-check mr-1" />Published</> : 'Publish'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-dismiss="modal">Done</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
